import type {ColumnFragment, ControlCardReferenceInput, ValueInstanceFragment} from "app/gql/graphqlSchema";
import {IonButton, IonCardContent, IonCardHeader, IonCardSubtitle, IonCardTitle, IonIcon, IonList} from "@ionic/react";
import {isNil, isNotBlank} from "app/utils/stdlib";
import {FlexColumn, FlexColumnCenter} from "app/employee/flexUtils";
import {useControlCardInstanceContext} from "app/employee/controlCard/ControlCardInstanceContext";
import {useHasControlCardValueError} from "app/employee/controlCard/hook/useHasControlCardValueError";
import {InputIonCard} from "app/employee/controlCard/input/InputIonCard";
import {DisplayValueInstanceError} from "app/employee/controlCard/input/DisplayValueInstanceError";
import {FstIonModal} from "app/employee/incdoc/FstIonModal";
import {AddEditTicket} from "app/employee/ticket/AddEditTicket";
import {TicketItem} from "app/employee/ticket/TicketItem";
import {useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import {chatbubblesOutline} from "ionicons/icons";
import {styled} from "styled-components";
import {useSaveControlCardValue} from "app/employee/controlCard/hook/useSaveControlCardValue";

interface Props {
  column: ColumnFragment & {__typename: "ControlCardTicketColumn"};
  valueInstance: ValueInstanceFragment;
  disabled?: boolean;
}

const StyleIonList = styled(IonList)`
  border-radius: 0.5rem;
  padding: 0;
  border: 1px solid var(--ion-color-medium);
`;

export const InputTicket = ({column, valueInstance, disabled}: Props) => {
  const {t} = useTranslation();
  const {instance, isComplete, canEdit, refresh} = useControlCardInstanceContext();
  const hasError = useHasControlCardValueError(valueInstance);
  const modalRef = useRef<HTMLIonModalElement>(null);
  const {revalidateValue} = useSaveControlCardValue(valueInstance);

  const localDisabled = isComplete || !!disabled || !canEdit;
  const ticket = valueInstance.ticket;

  const controlCardReference = useMemo(
    (): ControlCardReferenceInput => ({
      instanceId: instance.id,
      rowInstanceId: valueInstance.rowId,
      columnInstanceId: valueInstance.columnId
    }),
    [instance.id, valueInstance.rowId, valueInstance.columnId]
  );

  const handleCompleted = async () => {
    modalRef.current?.dismiss();
    await refresh();
    await revalidateValue();
  };

  return (
    <>
      <InputIonCard $hasError={hasError} data-scroll-to-value-instance-id={valueInstance.id}>
        <IonCardHeader>
          <IonCardTitle>{column.title}</IonCardTitle>
          {isNotBlank(column.description) && <IonCardSubtitle>{column.description}</IonCardSubtitle>}
        </IonCardHeader>
        <IonCardContent>
          <FlexColumn>
            {isNil(ticket) ? (
              <FlexColumnCenter>
                <IonButton disabled={localDisabled} onClick={() => modalRef.current?.present()} size="large">
                  <IonIcon icon={chatbubblesOutline} slot="start" />
                  {t("ticket.create-from-cc")}
                </IonButton>
              </FlexColumnCenter>
            ) : (
              <StyleIonList>
                <TicketItem lines="none" ticket={ticket} />
              </StyleIonList>
            )}
            <FlexColumnCenter>
              <DisplayValueInstanceError column={column} disabled={localDisabled} valueInstance={valueInstance} />
            </FlexColumnCenter>
          </FlexColumn>
        </IonCardContent>
      </InputIonCard>
      <FstIonModal ref={modalRef}>
        <AddEditTicket
          controlCardReference={controlCardReference}
          defaultLocationId={instance.location.id}
          onCompleted={handleCompleted}
          recipientGroupKeys={column.groupKeys}
        />
      </FstIonModal>
    </>
  );
};
